import { useEffect, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { portalApi, clearPortalToken, getPortalToken } from "@/lib/portalApi";
import { BRAND } from "@/lib/brandingConfig";
import { LogOut, Home, ClipboardCheck, CalendarCheck, HandHeart } from "lucide-react";

/**
 * Shared shell for /portal/* pages. Loads the signed-in Care Seeker's
 * profile once, gates the page behind auth, and renders a friendly
 * navy/sage header + bottom-tab navigation.
 */
export default function PortalLayout() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!getPortalToken()) {
      navigate("/portal/expired", { replace: true });
      return;
    }
    portalApi.get("/portal/me")
      .then(({ data }) => setMe(data))
      .catch(() => navigate("/portal/expired", { replace: true }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const signOut = () => {
    clearPortalToken();
    navigate("/portal/expired", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center" data-testid="portal-loading">
        <div className="text-[#1E3A8A]">Loading your portal…</div>
      </div>
    );
  }
  if (!me) return null;

  const tabs = [
    { to: "/portal",                   label: "Home",      Icon: Home,           testId: "portal-tab-home" },
    { to: "/portal/assessment",        label: "Check-in",  Icon: ClipboardCheck, testId: "portal-tab-assessment" },
    { to: "/portal/appointments",      label: "Visits",    Icon: CalendarCheck,  testId: "portal-tab-appointments" },
    { to: "/portal/request-help",      label: "Request",   Icon: HandHeart,      testId: "portal-tab-request" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24 md:pb-8">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E0]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt={BRAND.logoAlt}
              className="h-10 w-auto"
              data-testid="portal-brand-logo"
            />
            <div>
              <div className="font-display text-lg font-semibold text-[#0F172A] leading-tight">HarborWell Connect</div>
              <div className="text-xs text-[#7E937D]">Hi, {me.first_name || "friend"} · {me.display_id || ""}</div>
            </div>
          </div>
          <button
            onClick={signOut}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-[#334155] hover:bg-[#F9F8F6]"
            data-testid="portal-signout-btn"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Desktop sub-nav */}
      <nav className="hidden md:block bg-white border-b border-[#E5E5E0]">
        <div className="max-w-3xl mx-auto px-4 py-2 flex gap-1">
          {tabs.map(({ to, label, Icon, testId }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  active ? "bg-[#E8ECE7] text-[#1E3A8A]" : "text-[#334155] hover:bg-[#F9F8F6]"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Page body */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        <Outlet context={{ me, refresh: () => portalApi.get("/portal/me").then(({ data }) => setMe(data)) }} />
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E0] z-30">
        <div className="grid grid-cols-4">
          {tabs.map(({ to, label, Icon, testId }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={`${testId}-mobile`}
                className={`flex flex-col items-center gap-1 py-2.5 text-[11px] ${active ? "text-[#1E3A8A]" : "text-[#334155]"}`}
              >
                <Icon className="w-5 h-5" strokeWidth={active ? 2 : 1.5} /> {label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
